import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { DeleteButton, PlusButton } from "components/atoms/Buttons";
import { PaziaTimeInput } from "components/atoms/Inputs";
import { TopicOption, useCourseGroupsOptions } from "api/hooks";
import {
  PaziaCheckbox,
  PaziaFormButton,
  PaziaLoader,
  PaziaMultiSelect,
} from "components/atoms";
import { minutesToTime } from "utils";
import { CreateTopicModal } from "components/templates";
import { TopicApi } from "api";
import { PaziaButton, PaziaSelect, PaziaInput } from "components/atoms";
import { TopicGroupApi } from "api/topicgroups/topicgroupsApi";
import { routes } from "consts";
import { PaziaBox } from "components/atoms";

interface TopicGroupFormProps {
  onSubmit: (data: TopicGroupFormValues) => void;
  topicOptions: TopicOption[];
  topicGroup?: TopicGroupApi;
}

export const TopicGroupForm = ({ onSubmit, topicGroup, topicOptions }: TopicGroupFormProps) => {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [TOPIC_GROUP_FORM_NAMES.NAME]: topicGroup?.name,
      [TOPIC_GROUP_FORM_NAMES.TOPICS]: topicGroup?.topics,
      [TOPIC_GROUP_FORM_NAMES.COURSE_GROUP_IDS]: topicGroup?.courseGroups.map((x) => x.courseGroupId + ""),
      [TOPIC_GROUP_FORM_NAMES.GENERAL_TIME_FOR_GROUP]: !!topicGroup?.time,
      [TOPIC_GROUP_FORM_NAMES.GROUP_TIME]: minutesToTime(topicGroup?.time),
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: TOPIC_GROUP_FORM_NAMES.TOPICS,
    keyName: "key",
  });
  const { handleSubmit, setValue, watch } = methods;
  const { loading: loadingCourseGroups, options: courseGroupsOptions } = useCourseGroupsOptions();
  const generalTimeForGroup = watch(TOPIC_GROUP_FORM_NAMES.GENERAL_TIME_FOR_GROUP);
  const topic = watch(TOPIC_GROUP_FORM_NAMES.TOPIC);

  const onAdd = () => {
    const topicToAdd = topicOptions.find((x) => x.value == topic);
    if (!topicToAdd) return;
    append(topicToAdd.topic!);
    setValue(TOPIC_GROUP_FORM_NAMES.TOPIC, "");
  };

  const onSuccessModalCreation = (resTopic: TopicApi) => {
    append(resTopic);
    setValue(TOPIC_GROUP_FORM_NAMES.TOPIC, "");
  };

  if (loadingCourseGroups) return <PaziaLoader position="center" />;
  return (
    <PaziaBox>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex grid-cols-2 flex-col gap-8 md:grid">
          <div className="grid max-w-xl auto-rows-min gap-2">
            <div className="text-primary-500 mb-2 text-center text-sm uppercase">Dane podstawowe</div>
            <PaziaInput name={TOPIC_GROUP_FORM_NAMES.NAME} label="Nazwa" type="text" />
            <PaziaMultiSelect
              name={TOPIC_GROUP_FORM_NAMES.COURSE_GROUP_IDS}
              options={courseGroupsOptions}
              label="Grupa kursu"
            />
          </div>
          <div className="flex auto-rows-min flex-col gap-2">
            <div className="text-primary-500 mb-2 text-center text-sm uppercase">Lista tematów</div>
            <div className="flex w-full gap-1">
              <PaziaSelect
                className="w-full"
                name={TOPIC_GROUP_FORM_NAMES.TOPIC}
                options={topicOptions.filter((x) => !fields.find((f) => f.id == x.value))}
                noOptionsMessage="Brak tematów do dodania — utwórz je w Tematy"
              />
              <div className="flex items-end">
                <PlusButton onClick={onAdd} type="button" />
              </div>
            </div>
            {fields.length === 0 && (
              <p data-testid="topic-group-empty-hint" className="text-sm text-slate-500 dark:text-slate-400">
                Wybierz temat z listy i kliknij „+”. Grupę można zapisać, gdy ma co najmniej jeden temat.
              </p>
            )}
            {fields.length > 0 && (
              <>
                {/* todo usuwanie z grupy */}
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="rounded border border-slate-200 bg-white px-4 py-3 text-left text-sm shadow-sm duration-150 ease-in-out hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600"
                  >
                    <div className="mb-0.5 flex flex-col">
                      <div className="flex justify-between">
                        <span className="font-semibold text-slate-800 dark:text-slate-100">
                          {index + 1}.{" "}
                          <a
                            target="_blank"
                            href={routes.TOPICS_EDIT(field.id?.toString())}
                            className="hover:underline"
                          >
                            {" "}
                            {field.title}
                          </a>
                        </span>
                        <DeleteButton onClick={() => remove(index)} />
                        {/* <PaziaButton
                          btnType="button"
                          title="Usuń"
                          type="danger2"
                          className="mt-2"
                          onClick={() => remove(index)}
                        /> */}
                      </div>
                      <div>
                        <span className="font-medium text-emerald-600">
                          {minutesToTime(field.time?.toString() ?? "")}
                        </span>
                        {/* {field.description && <div className="text-sm">{field.description}</div>} */}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="flex h-[2rem] items-center justify-between">
                  <div
                    onClick={() => setValue(TOPIC_GROUP_FORM_NAMES.GENERAL_TIME_FOR_GROUP, !generalTimeForGroup)}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <PaziaCheckbox name={TOPIC_GROUP_FORM_NAMES.GENERAL_TIME_FOR_GROUP} />
                    Ustaw czas trwania całej grupy
                  </div>
                  {generalTimeForGroup && (
                    <div className="flex w-[6rem] justify-center">
                      <PaziaTimeInput name={TOPIC_GROUP_FORM_NAMES.GROUP_TIME} />
                    </div>
                  )}
                </div>
                <div className="flex justify-end pt-2">
                  <PaziaFormButton title="Zapisz" type="success" />
                </div>
              </>
            )}
          </div>
        </form>
      </FormProvider>
      <CreateTopicModal
        isOpen={topic == "-1"}
        onCloseRequest={() => setValue(TOPIC_GROUP_FORM_NAMES.TOPIC, "")}
        onSuccess={onSuccessModalCreation}
      />
    </PaziaBox> //TODO na modalu bez boxa
  );
};

const TOPIC_GROUP_FORM_NAMES = {
  NAME: "name",
  COURSE_GROUP_IDS: "courseGroupIds",
  GENERAL_TIME_FOR_GROUP: "generalTimeForGroup",
  GROUP_TIME: "groupTime",
  TOPIC: "topic",
  TOPICS: "topics",
} as const;

const schema = yup
  .object({
    [TOPIC_GROUP_FORM_NAMES.NAME]: yup.string().required(),
    [TOPIC_GROUP_FORM_NAMES.GROUP_TIME]: yup
      .string()
      .nullable()
      .when(TOPIC_GROUP_FORM_NAMES.GENERAL_TIME_FOR_GROUP, {
        is: true,
        then: (shema) => shema.required(),
      }),
    [TOPIC_GROUP_FORM_NAMES.GENERAL_TIME_FOR_GROUP]: yup.boolean(),
    [TOPIC_GROUP_FORM_NAMES.TOPIC]: yup.string(),
    [TOPIC_GROUP_FORM_NAMES.COURSE_GROUP_IDS]: yup.array().of(yup.string()),
    [TOPIC_GROUP_FORM_NAMES.TOPICS]: yup
      .array()
      .of(
        yup.object().shape({
          time: yup.number().nullable(),
          title: yup.string(),
          description: yup.string(),
          id: yup.number(),
        }),
      )
      .required(),
  })
  .required();
export type TopicGroupFormValues = yup.InferType<typeof schema>;
